import { useCallback, useEffect, useState } from 'react';
import { useAutonomousKartStore, useHudStore } from '../../store/sceneStore';
import { useSendUserInput } from '../../provider/socketContext';

export const TargetSpeed = ({ disabled }: { disabled: boolean }) => {
	const speed_limit = useHudStore((s) => s.data.speed_limit);
	const { cruiseTargetSpeed } = useAutonomousKartStore();
	const sendUserInput = useSendUserInput();

	// Optimistic update: pending 값이 있으면 그걸 표시, 없으면 서버 값 표시
	const [pendingSpeed, setPendingSpeed] = useState<number | null>(null);
	const [isFlashing, setIsFlashing] = useState(false);
	const displaySpeed = pendingSpeed ?? cruiseTargetSpeed ?? 0;

	// 서버 값이 pending 값과 같아지면 pending 해제
	useEffect(() => {
		if (pendingSpeed !== null && cruiseTargetSpeed === pendingSpeed) {
			setPendingSpeed(null);
		}
	}, [cruiseTargetSpeed, pendingSpeed]);

	const handleSpeedChange = useCallback(
		(delta: number) => {
			const newSpeed = Math.max(Math.min(speed_limit, displaySpeed + delta), 0);

			// 속도 제한을 초과하려고 할 때 깜빡임 효과
			if (displaySpeed + delta > speed_limit) {
				setIsFlashing(true);
				setTimeout(() => setIsFlashing(false), 2000);
				return;
			}

			setPendingSpeed(newSpeed); // optimistic update
			sendUserInput({
				type: 'user_input',
				text: 'set_target_speed',
				value: newSpeed,
			});
		},
		[speed_limit, displaySpeed, sendUserInput],
	);

	return (
		<div className='flex flex-col items-center justify-between w-full h-full'>
			<span className='text-zinc-400 text-6xl font-bold mb-4'>목표 속도</span>
			<div className='flex items-center justify-center gap-8 w-full h-full'>
				<button
					onClick={() => handleSpeedChange(-1)}
					disabled={disabled}
					className='relative h-60 w-40 rounded-3xl bg-zinc-700 disabled:opacity-30 enabled:hover:bg-zinc-600 enabled:active:bg-zinc-500 transition flex items-center justify-center shadow-lg before:absolute before:inset-0 before:scale-150 before:rounded-3xl'
					aria-label='속도 감소'
				>
					<MinusIcon />
				</button>
				<div className='flex flex-col items-center justify-center w-60'>
					<span
						className={`text-[8rem] font-bold leading-none tabular-nums transition-colors duration-200 ${isFlashing ? 'text-red-500 animate-pulse' : 'text-white'}`}
					>
						{displaySpeed}
					</span>
					<span className='text-4xl text-zinc-400 mt-2'>km/h</span>
				</div>
				<button
					onClick={() => handleSpeedChange(1)}
					disabled={disabled}
					className='relative h-60 w-40 rounded-3xl bg-zinc-700 disabled:opacity-30 enabled:hover:bg-zinc-600 enabled:active:bg-zinc-500 transition flex items-center justify-center shadow-lg before:absolute before:inset-0 before:scale-150 before:rounded-3xl'
					aria-label='속도 증가'
				>
					<PlusIcon />
				</button>
			</div>
		</div>
	);
};

const PlusIcon = () => (
	<svg
		xmlns='http://www.w3.org/2000/svg'
		className='h-24 w-24 text-white'
		fill='none'
		viewBox='0 0 24 24'
		stroke='currentColor'
		strokeWidth={3}
	>
		<path
			strokeLinecap='round'
			strokeLinejoin='round'
			d='M12 4.5v15m7.5-7.5h-15'
		/>
	</svg>
);
const MinusIcon = () => (
	<svg
		xmlns='http://www.w3.org/2000/svg'
		className='h-24 w-24 text-white'
		fill='none'
		viewBox='0 0 24 24'
		stroke='currentColor'
		strokeWidth={3}
	>
		<path strokeLinecap='round' strokeLinejoin='round' d='M19.5 12h-15' />
	</svg>
);
